import axios from '../config/axiosInstance';

export interface TrainingComment {
    id: string;
    entryId: string;
    userId: string;
    senderName: string;
    senderRole: string;
    message: string;
    createdAt: string;
}

export interface TrainingEntryView {
    id: string;
    userId: string;
    trainerId: string;
    trainingDate: string;
    title: string;
    description: string | null;
    activityLink: string | null;
    userComment: string | null;
    sport: string | null;
    /** Entry exists only to host comments on a day with no exercise assigned. */
    isPlaceholder: boolean;
    completed: boolean;
    durationHours: number | null;
    createdAt: string;
    updatedAt: string;
}

export interface TrainingDayView {
    date: string;
    dayName: string;
    isToday: boolean;
    isPast: boolean;
    isRestDay: boolean;
    isEditableForTrainer: boolean;
    entries: TrainingEntryView[];
}

export interface TrainingWeekResponse {
    user: {
        id: string;
        firstName: string;
        lastName: string;
        email: string;
        restDay: string | null;
    };
    weekStart: string;
    weekEnd: string;
    programLabel: string | null;
    stats: {
        totalSessions: number;
        completedSessions: number;
        upcomingSessions: number;
        hoursTrained: number;
    };
    days: TrainingDayView[];
}

const trainingService = {
    async getMyWeek(date: string): Promise<TrainingWeekResponse> {
        const response = await axios.get('/training/me/week', {
            params: { date },
        });
        return response.data;
    },

    async updateMyComment(entryId: string, userComment: string): Promise<TrainingEntryView> {
        const response = await axios.patch(`/training/entries/${entryId}/comment`, {
            userComment,
        });
        return response.data;
    },

    async markComplete(
        entryId: string,
        completed: boolean,
        durationHours?: number,
    ): Promise<TrainingEntryView> {
        const response = await axios.patch(`/training/entries/${entryId}/complete`, {
            completed,
            durationHours,
        });
        return response.data;
    },

    async getComments(entryId: string): Promise<TrainingComment[]> {
        const response = await axios.get(`/training/entries/${entryId}/comments`);
        return response.data;
    },

    async addComment(entryId: string, message: string): Promise<TrainingComment> {
        const response = await axios.post(`/training/entries/${entryId}/comments`, { message });
        window.dispatchEvent(new Event('comment-posted'));
        return response.data;
    },

    async deleteComment(commentId: string): Promise<void> {
        await axios.delete(`/training/comments/${commentId}`);
    },

    /**
     * Comment on a day that has no exercise assigned. The backend creates a
     * placeholder entry to host the thread, so this works on empty and rest days.
     */
    async addDayComment(userId: string, date: string, message: string): Promise<TrainingComment> {
        const response = await axios.post(`/training/users/${userId}/day-comments`, {
            date,
            message,
        });
        window.dispatchEvent(new Event('comment-posted'));
        return response.data;
    },
};

export default trainingService;
